import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useProcessos from "../../hooks/useProcessos";
import useTabelaProcessos from "../../hooks/useTabelaProcessos";
import useUsuarios from "../../hooks/useUsuarios";
import { useAuth } from "../../auth/AuthContext";
import HistoricoProcessual from "../../components/processo/HistoricoProcessual";
import { formatarNomeUpper } from "../../utils/formatadores";

const ConsultarProcessos = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { usuarios } = useUsuarios();

  // Passando 'usuarios' para o hook para que ele possa filtrar também pelo nome do advogado
  const {
    setBusca,
    paginaAtual,
    setPaginaAtual,
    processosPagina,
    totalPaginas,
    deletarProcesso,
    editarProcesso
  } = useProcessos(true, usuarios);

  const [modalAberto, setModalAberto] = useState(false);
  const [processoEditando, setProcessoEditando] = useState(null);
  const [modoEdicao, setModoEdicao] = useState(false);

  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [processoHistorico, setProcessoHistorico] = useState(null);
  const [processoOriginal, setProcessoOriginal] = useState(null);

  const { colunas, linhas } = useTabelaProcessos(processosPagina, usuarios);
  const [inputBusca, setInputBusca] = useState("");
  const [actionType, setActionType] = useState(null);

  const abrirDetalhes = (processo) => {
    setProcessoEditando({ ...processo });
    setModoEdicao(false);
    setModalAberto(true);
  };

  const abrirHistorico = (processo) => {
    setProcessoHistorico(processo);
    setHistoricoAberto(true);
  };

  const iniciarEdicao = () => {
    setProcessoOriginal({ ...processoEditando });
    setModoEdicao(true);
  };

  const cancelarEdicao = () => {
    setProcessoEditando({ ...processoOriginal });
    setModoEdicao(false);
  };

  const fecharModalDetalhes = () => {
    setModalAberto(false);
    setModoEdicao(false);
    setProcessoEditando(null);
    setProcessoOriginal(null);
  };

  const salvarEdicao = async (e) => {
    if (e) e.preventDefault();

    if (!processoOriginal || !processoEditando) return;

    if (!processoEditando.clienteId) {
      alert("Selecione um cliente");
      return;
    }

    if (!processoEditando.advogadoId) {
      alert("Selecione um advogado responsável");
      return;
    }

    const alteracoes = {};

    Object.keys(processoEditando).forEach((campo) => {
      const valorOriginal = processoOriginal[campo];
      let valorAtual = processoEditando[campo];

      if (campo === "parceiroId") {
        valorAtual = processoEditando.temParceiro && processoEditando.parceiroId ? processoEditando.parceiroId : null;
      }

      if (JSON.stringify(valorOriginal) !== JSON.stringify(valorAtual)) {
        alteracoes[campo] = valorAtual;
      }
    });

    if (Object.keys(alteracoes).length === 0) {
      setModoEdicao(false);
      return;
    }

    setActionType("editar");

    try {
      await editarProcesso(processoEditando._id, alteracoes);
      alert("Processo atualizado com sucesso!");
      setModoEdicao(false);
    } catch (err) {
      console.error(err);
      alert("Erro ao atualizar processo");
    } finally {
      setActionType(null);
    }
  };

  useEffect(() => {
    const verificarSessao = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/me`, {
          method: "GET",
          credentials: "include"
        });

        if (!res.ok) {
          navigate("/login");
        }
      } catch (error) {
        console.error("Erro de rede ou backend indisponível", error);
      }
    };

    verificarSessao();
  }, [navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setBusca(inputBusca);
      setPaginaAtual(1);
    }, 600);

    return () => clearTimeout(timer);
  }, [inputBusca, setBusca, setPaginaAtual]);

  const formatarMoeda = (valor) => {
    if (valor === null || valor === undefined || valor === "") return "";
    return Number(valor).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL"
    });
  };

  const formatarData = (data) => {
    if (!data) return "";
    const dataObj = new Date(data);
    if (Number.isNaN(dataObj.getTime())) return data;
    return dataObj.toLocaleDateString("pt-BR");
  };

  const campoDesabilitado = !modoEdicao;

  return (
    <div className="container-consulta-processos">
      <div className="card">
        <h1>CONSULTAR PROCESSOS</h1>

        <div className="search-bar">
          <input
            type="text"
            placeholder="Buscar por número, autor, área, notificação ou advogado"
            value={inputBusca}
            onChange={(e) => setInputBusca(e.target.value)}
          />
        </div>

        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                {colunas.map((coluna) => (
                  <th key={coluna.chave}>{coluna.titulo}</th>
                ))}
                <th className="th-acoes">Ações</th>
              </tr>
            </thead>

            <tbody>
              {linhas.map((linha) => (
                <tr key={linha.id} className={linha.prioridade?.classe || ""}>
                  {colunas.map((coluna) => {
                    const valorCelular = linha[coluna.chave];

                    if (coluna.chave === "nomeNotificacao") {
                      const { texto, urgencia, resolvido } =
                        typeof valorCelular === "object"
                          ? valorCelular || {}
                          : {
                              texto: valorCelular,
                              urgencia: "MEDIA",
                              resolvido: false
                            };

                      return (
                        <td key={coluna.chave}>
                          <span
                            className={`badge-notificacao ${urgencia?.toLowerCase()} ${
                              resolvido ? "resolvido" : ""
                            }`}
                          >
                            {texto || "Sem Notificação"}
                          </span>
                        </td>
                      );
                    }

                    if (coluna.chave === "prazoFinal") {
                      const { dataFormatada, diasRestantes, status, editada, cumprido } = valorCelular || {};

                      if (status === "sem-prazo" || !dataFormatada) {
                        return <td key={coluna.chave}>—</td>;
                      }

                      if (cumprido) {
                        return (
                          <td key={coluna.chave}>
                            <div
                              className="tag-prazo cumprido"
                              style={{ backgroundColor: "#28a745", color: "#ffffff", padding: "4px 8px", borderRadius: "4px" }}
                              title="Notificação/Prazo marcado como cumprido"
                            >
                              <span className="data-prazo">{dataFormatada}</span>
                              <span className="legenda-prazo"> (Cumprido)</span>
                            </div>
                          </td>
                        );
                      }

                      let legendaPrazo = "";
                      if (status === "vencido") {
                        legendaPrazo = ` (${Math.abs(diasRestantes)}d atrasado)`;
                      } else if (status === "hoje") {
                        legendaPrazo = " (Hoje!)";
                      } else if (status === "alerta") {
                        legendaPrazo = ` (${diasRestantes}d restantes)`;
                      } else if (status === "no-prazo") {
                        legendaPrazo = ` (${diasRestantes}d)`;
                      }

                      const textoTooltip = editada
                        ? "Data confirmada/editada manualmente"
                        : "Data estimada automaticamente pelo sistema";

                      return (
                        <td key={coluna.chave}>
                          <div
                            className={`tag-prazo ${status} ${linha.prioridade?.classe || ""}`}
                            title={textoTooltip}
                          >
                            <span className="data-prazo">{dataFormatada}</span>
                            <span className="legenda-prazo">{legendaPrazo}</span>
                          </div>
                        </td>
                      );
                    }
                    return <td key={coluna.chave}>{valorCelular}</td>;
                  })}

                  <td className="td-acoes">
                    <div className="action-buttons">
                      <button
                        className="btn-action btn-edit"
                        title="Exibir detalhes do processo"
                        aria-label="Exibir detalhes do processo"
                        onClick={() => abrirDetalhes(linha.processoOriginal)}
                      >
                        🔎
                      </button>

                      <button
                        className="btn-action btn-notify"
                        title="Notificações / Histórico"
                        aria-label="Notificações e histórico"
                        onClick={() => abrirHistorico(linha.processoOriginal)}
                      >
                        🔔
                      </button>

                      <button
                        className="btn-action btn-delete"
                        title={user?.isAdmin ? "Excluir processo" : "Apenas administradores podem excluir"}
                        aria-label="Excluir processo"
                        onClick={() => deletarProcesso(linha.id)}
                        disabled={!user?.isAdmin}
                      >
                        🗑
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          {Array.from({ length: totalPaginas }, (_, i) => (
            <button
              key={i}
              className={paginaAtual === i + 1 ? "active-page" : ""}
              onClick={() => setPaginaAtual(i + 1)}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>

      {modalAberto && processoEditando && (
        <div className="modal-overlay">
          <div className="modal modal-editar-wide">
            <div className="modal-header-editar">
              <h2>{modoEdicao ? "EDITAR PROCESSO" : "DETALHES DO PROCESSO"}</h2>
            </div>

            <div className="form-grid-wide">
              <div className="form-group">
                <label>Número do Processo</label>
                <input disabled type="text" value={processoEditando.numeroProcesso || ""} className="input-disabled" />
              </div>

              <div className="form-group">
                <label>Data do Ajuizamento</label>
                <input
                  disabled
                  type="text"
                  value={formatarData(processoEditando.dataAjuizamento)}
                  className="input-disabled"
                />
              </div>

              <div className="form-group">
                <label>Autor</label>
                <input
                  disabled
                  type="text"
                  value={formatarNomeUpper(processoEditando.autor)}
                  className="input-disabled"
                />
              </div>

              <div className="form-group">
                <label>Advogado</label>
                <select
                  disabled={!modoEdicao}
                  value={
                    typeof processoEditando.advogadoId === "object"
                      ? processoEditando.advogadoId?._id || ""
                      : processoEditando.advogadoId || ""
                  }
                  onChange={(e) =>
                    setProcessoEditando({ ...processoEditando, advogadoId: e.target.value })
                  }
                  className={!modoEdicao ? "input-disabled" : ""}
                >
                  <option value="">Selecione o Advogado</option>
                  {usuarios.map((usr) => (
                    <option key={usr._id || usr.id} value={usr._id || usr.id}>
                      {formatarNomeUpper(usr.nome)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Valor da Causa</label>
                <input
                  disabled
                  type="text"
                  value={formatarMoeda(processoEditando.valorCausa)}
                  className="input-disabled"
                />
              </div>

              <div className="form-group">
                <label>Área do Direito</label>
                <select
                  disabled={campoDesabilitado}
                  value={(processoEditando.areaDireito || "").toLowerCase()}
                  onChange={(e) =>
                    setProcessoEditando({ ...processoEditando, areaDireito: e.target.value })
                  }
                  className={campoDesabilitado ? "input-disabled" : ""}
                >
                  <option value="">Selecione</option>
                  <option value="trabalhista">Trabalhista</option>
                  <option value="previdenciario">Previdenciário</option>
                  <option value="civel">Cível</option>
                  <option value="criminal">Criminal</option>
                  <option value="administrativo">Administrativo</option>
                </select>
              </div>

              <div className="form-group">
                <label>Fase da Ação</label>
                <select
                  disabled={campoDesabilitado}
                  value={(processoEditando.faseAcao || "").toLowerCase()}
                  onChange={(e) =>
                    setProcessoEditando({ ...processoEditando, faseAcao: e.target.value })
                  }
                  className={campoDesabilitado ? "input-disabled" : ""}
                >
                  <option value="">Selecione</option>
                  <option value="administrativo">Administrativo</option>
                  <option value="protocolo">Protocolo</option>
                  <option value="audiencia_conciliacao">Audiência de Conciliação</option>
                  <option value="instrucao">Instrução</option>
                  <option value="sentenca">Sentença</option>
                  <option value="execucao">Execução</option>
                  <option value="arquivado">Arquivado</option>
                </select>
              </div>

              <div className="form-group">
                <label>Possui Advogado Parceiro?</label>
                <select
                  disabled={campoDesabilitado}
                  value={processoEditando.temParceiro ? "sim" : "nao"}
                  onChange={(e) =>
                    setProcessoEditando({
                      ...processoEditando,
                      temParceiro: e.target.value === "sim"
                    })
                  }
                  className={campoDesabilitado ? "input-disabled" : ""}
                >
                  <option value="nao">Não</option>
                  <option value="sim">Sim</option>
                </select>
              </div>

              <div className="form-group">
                <label>Advogado Parceiro</label>
                <select
                  disabled={campoDesabilitado || !processoEditando.temParceiro}
                  value={
                    typeof processoEditando.parceiroId === "object"
                      ? processoEditando.parceiroId?._id || ""
                      : processoEditando.parceiroId || ""
                  }
                  onChange={(e) =>
                    setProcessoEditando({ ...processoEditando, parceiroId: e.target.value })
                  }
                  className={
                    campoDesabilitado || !processoEditando.temParceiro ? "input-disabled" : ""
                  }
                >
                  <option value="">Selecione o Parceiro</option>
                  {usuarios.map((usr) => (
                    <option key={usr._id || usr.id} value={usr._id || usr.id}>
                      {formatarNomeUpper(usr.nome)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Quem Ajuizou</label>
                <input
                  disabled
                  type="text"
                  value={
                    typeof processoEditando.quemAjuizouId === "object"
                      ? formatarNomeUpper(processoEditando.quemAjuizouId?.nome) || ""
                      : usuarios.find(
                          (u) => (u._id || u.id)?.toString() === processoEditando.quemAjuizouId?.toString()
                        )?.nome
                      ? formatarNomeUpper(
                          usuarios.find(
                            (u) => (u._id || u.id)?.toString() === processoEditando.quemAjuizouId?.toString()
                          )?.nome
                        )
                      : "Não informado"
                  }
                  className="input-disabled"
                />
              </div>

              <div className="form-group">
                <label>Criado em</label>
                <input disabled type="text" value={formatarData(processoEditando.createdAt)} className="input-disabled" />
              </div>

              <div className="form-group">
                <label>Última atualização</label>
                <input disabled type="text" value={formatarData(processoEditando.updatedAt)} className="input-disabled" />
              </div>
            </div>

            <div className="modal-actions">
              {!modoEdicao ? (
                <>
                  <button 
                    className="btn-cancel" 
                    onClick={fecharModalDetalhes}
                    disabled={actionType !== null}
                  >
                    Fechar
                  </button>
                  <button 
                    className="btn-save" 
                    onClick={iniciarEdicao}
                    disabled={actionType !== null}
                  >
                    ✏️ Editar Processo
                  </button>
                </>
              ) : (
                <>
                  <button 
                    className="btn-cancel" 
                    onClick={cancelarEdicao}
                    disabled={actionType !== null}
                  >
                    Cancelar
                  </button>
                  <button 
                    className="btn-save" 
                    onClick={salvarEdicao}
                    disabled={actionType !== null}
                  >
                    {actionType === "editar" ? (
                      <span className="spinner-loader"></span>
                    ) : (
                      "Salvar Alterações"
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <HistoricoProcessual
        aberto={historicoAberto}
        processo={processoHistorico}
        usuarios={usuarios}
        onSalvarProcesso={editarProcesso}
        onFechar={() => {
          setHistoricoAberto(false);
          setProcessoHistorico(null);
        }}
      />
    </div>
  );
};

export default ConsultarProcessos;